import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { useAppSelector } from 'hooks/reduxHooks'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getIsLoyaltyLuxPlusTieredRenewalRewardEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusTieredRenewalRewardEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { getBaseTierOfferWithJoiningFee, getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import LuxPlusManageAccountModalMembershipCancellation from './LuxPlusManageAccountModalMembershipCancellation'
import LuxPlusManageAccountModalOverview from './LuxPlusManageAccountModalOverview'
import LuxPlusManageAccountModalRenewalCancellation from './LuxPlusManageAccountModalRenewalCancellation'
import LuxPlusManageAccountModalRenewalCancellationSurvey from './LuxPlusManageAccountModalRenewalCancellationSurvey'
import LuxPlusManageAccountModalRenewalCancellationConfirmation from './LuxPlusManageAccountModalRenewalCancellationConfirmation'
import LuxPlusManageAccountModalKeepBenefits from './LuxPlusManageAccountModalKeepBenefits'
import LuxPlusManageAccountModalKeepPrice from './LuxPlusManageAccountModalKeepPrice'
import LuxPlusManageAccountModalRenewalBonusRetention from './LuxPlusManageAccountModalRenewalBonusRetention'
import LuxPlusManageAccountModalRenewalCancellationSuccess from './LuxPlusManageAccountModalRenewalCancellationSuccess'
import LuxPlusManageAccountModalRenewalCancellationSuccessTiered from './LuxPlusManageAccountModalRenewalCancellationSuccessTiered'
import LuxPlusManageAccountModalRenewalReactivation from './LuxPlusManageAccountModalRenewalReactivation'
import LuxPlusManageAccountModalRenewalReactivationSuccess from './LuxPlusManageAccountModalRenewalReactivationSuccess'
import LuxPlusManageAccountModalRenewalReactivationSuccessTiered from './LuxPlusManageAccountModalRenewalReactivationSuccessTiered'
import LuxPlusManageAccountModalUpdatePaymentMethod from './LuxPlusManageAccountModalUpdatePaymentMethod'
import LuxPlusManageAccountModalUpdatePaymentMethodFailure from './LuxPlusManageAccountModalUpdatePaymentMethodFailure'
import LuxPlusManageAccountModalUpdatePaymentMethodSuccess from './LuxPlusManageAccountModalUpdatePaymentMethodSuccess'
import LuxPlusManageAccountModalAddBackupPaymentMethod from './LuxPlusManageAccountModalAddBackupPaymentMethod'

type ManageMode =
  | 'overview'
  | 'membership-cancellation'
  | 'renewal-cancellation'
  | 'renewal-cancellation-confirmation'
  | 'renewal-price-retention'
  | 'renewal-bonus-retention'
  | 'renewal-cancellation-success'
  | 'renewal-reactivation'
  | 'renewal-reactivation-success'
  | 'payment-method-update'
  | 'payment-method-update-success'
  | 'payment-method-update-failure'
  | 'backup-payment-method-add'

interface Props {
  initialMode?: ManageMode
  onBackupPaymentMethodUpdateSuccess?: (paymentMethodId?: string) => void
  onPaymentMethodUpdateSuccess?: () => void
  returnToOverviewOnActionComplete?: boolean
}

function LuxPlusManageAccountModal({
  initialMode = 'overview',
  onBackupPaymentMethodUpdateSuccess,
  onPaymentMethodUpdateSuccess,
  returnToOverviewOnActionComplete = true,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const [mode, setMode] = useState<ManageMode>(initialMode)
  const [paymentFailureBackMode, setPaymentFailureBackMode] = useState<ManageMode>('payment-method-update')
  const [paymentMethodsRefreshKey, setPaymentMethodsRefreshKey] = useState(0)
  const [expectedBackupPaymentMethodId, setExpectedBackupPaymentMethodId] = useState<string>()

  const { item } = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  const renewalDate = useAppSelector(getLuxPlusSubscriptionRenewalDate)

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const isTieredRenewalRewardEnabled = useAppSelector(getIsLoyaltyLuxPlusTieredRenewalRewardEnabled)

  // Before the cancel-reason step, surface the price-retention modal when the member's renewal offer
  // is cheaper than the current new-member (active) offer — i.e. they'd lose a locked-in lower price.
  const activeOfferPrice = useAppSelector(getBaseTierOfferWithJoiningFee)?.price
  const shouldShowKeepPrice = isTieredRenewalRewardEnabled
    && !!item?.currentRenewalOffer
    && activeOfferPrice !== undefined
    && item.currentRenewalOffer.price < activeOfferPrice

  // After the price step, Société members see their upcoming renewal bonus before cancelling.
  const isSocieteMember = useAppSelector(getHasLoyaltyAccount)
  const shouldShowRenewalBonus = isTieredRenewalRewardEnabled && isSocieteMember

  // First retention step a "continue to cancel" from the confirmation lands on, skipping any
  // that don't apply, finally falling through to the cancel-reason form.
  const firstRetentionStep: ManageMode = shouldShowKeepPrice
    ? 'renewal-price-retention'
    : shouldShowRenewalBonus
      ? 'renewal-bonus-retention'
      : 'renewal-cancellation'

  const returnToOverviewOrDismiss = useCallback(() => {
    if (returnToOverviewOnActionComplete) {
      setMode('overview')
    } else {
      modalElement.resolve()
    }
  }, [modalElement, returnToOverviewOnActionComplete])

  return <ModalBase size="S" height="max" tabletHeight="auto">
    {!item && <ModalContent>
      <ModalBody>
        <LoadingIndicator inline>{intl.formatMessage({
          id: 'p8Cm7g3Ju1H67rSjBhKW',
          defaultMessage: 'Fetching account information',
          description: 'Loading indicator text while fetching account subscription data',
        })}
        </LoadingIndicator>
      </ModalBody>
    </ModalContent>}
    {!!item && <>
      {mode === 'overview' && <LuxPlusManageAccountModalOverview
        expectedBackupPaymentMethodId={expectedBackupPaymentMethodId}
        item={item}
        onCancelMembership={() => setMode('membership-cancellation')}
        onCancelRenew={() => setMode('renewal-cancellation-confirmation')}
        onExpectedBackupPaymentMethodFound={() => setExpectedBackupPaymentMethodId(undefined)}
        onReactivateRenew={() => setMode('renewal-reactivation')}
        onUpdatePaymentMethod={() => setMode('payment-method-update')}
        onAddBackupPaymentMethod={() => setMode('backup-payment-method-add')}
        paymentMethodsRefreshKey={paymentMethodsRefreshKey}
      />}
      {mode === 'membership-cancellation' && <LuxPlusManageAccountModalMembershipCancellation
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-cancellation-confirmation' && isTieredRenewalRewardEnabled && <LuxPlusManageAccountModalKeepBenefits
        item={item}
        onCancelRenewal={() => setMode(firstRetentionStep)}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-cancellation-confirmation' && !isTieredRenewalRewardEnabled && <LuxPlusManageAccountModalRenewalCancellationConfirmation
        item={item}
        onCancelRenewal={() => setMode(firstRetentionStep)}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-price-retention' && <LuxPlusManageAccountModalKeepPrice
        item={item}
        onContinueToCancel={() => setMode(shouldShowRenewalBonus ? 'renewal-bonus-retention' : 'renewal-cancellation')}
        onBack={() => setMode('renewal-cancellation-confirmation')}
      />}
      {mode === 'renewal-bonus-retention' && <LuxPlusManageAccountModalRenewalBonusRetention
        onContinueToCancel={() => setMode('renewal-cancellation')}
        onBack={() => setMode(shouldShowKeepPrice ? 'renewal-price-retention' : 'renewal-cancellation-confirmation')}
      />}
      {mode === 'renewal-cancellation' && isTieredRenewalRewardEnabled && <LuxPlusManageAccountModalRenewalCancellationSurvey
        item={item}
        onRenewCancelSuccess={() => setMode('renewal-cancellation-success')}
      />}
      {mode === 'renewal-cancellation' && !isTieredRenewalRewardEnabled && <LuxPlusManageAccountModalRenewalCancellation
        item={item}
        onRenewCancelSuccess={() => setMode('renewal-cancellation-success')}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-cancellation-success' && isTieredRenewalRewardEnabled && <LuxPlusManageAccountModalRenewalCancellationSuccessTiered item={item} />}
      {mode === 'renewal-cancellation-success' && !isTieredRenewalRewardEnabled && <LuxPlusManageAccountModalRenewalCancellationSuccess
        item={item}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-reactivation' && <LuxPlusManageAccountModalRenewalReactivation
        item={item}
        onReactivationSuccess={() => isLoyaltyEnabled ? setMode('renewal-reactivation-success') : setMode('overview')}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-reactivation-success' && isTieredRenewalRewardEnabled && <LuxPlusManageAccountModalRenewalReactivationSuccessTiered />}
      {mode === 'renewal-reactivation-success' && !isTieredRenewalRewardEnabled && <LuxPlusManageAccountModalRenewalReactivationSuccess
        item={item}
        onBack={() => setMode('overview')}
      />}
      {mode === 'payment-method-update' && <LuxPlusManageAccountModalUpdatePaymentMethod
        item={item}
        renewalDate={renewalDate}
        onUpdatePaymentMethodSuccess={() => {
          setPaymentMethodsRefreshKey(refreshKey => refreshKey + 1)
          onPaymentMethodUpdateSuccess?.()
          setMode('payment-method-update-success')
        }}
        onUpdatePaymentMethodFailure={() => {
          setPaymentFailureBackMode('payment-method-update')
          setMode('payment-method-update-failure')
        }}
        onBack={returnToOverviewOrDismiss}
      />}
      {mode === 'payment-method-update-success' && <LuxPlusManageAccountModalUpdatePaymentMethodSuccess
        onBack={returnToOverviewOrDismiss}
      />}
      {mode === 'payment-method-update-failure' && <LuxPlusManageAccountModalUpdatePaymentMethodFailure
        onBack={() => setMode(paymentFailureBackMode)}
      />}
      {mode === 'backup-payment-method-add' && <LuxPlusManageAccountModalAddBackupPaymentMethod
        item={item}
        renewalDate={renewalDate}
        onSuccess={(paymentMethodId) => {
          setExpectedBackupPaymentMethodId(paymentMethodId)
          setPaymentMethodsRefreshKey(refreshKey => refreshKey + 1)
          onBackupPaymentMethodUpdateSuccess?.(paymentMethodId)
          returnToOverviewOrDismiss()
        }}
        onFailure={() => {
          setPaymentFailureBackMode('backup-payment-method-add')
          setMode('payment-method-update-failure')
        }}
        onBack={returnToOverviewOrDismiss}
      />}
    </>}
  </ModalBase>
}

export default LuxPlusManageAccountModal
